/**
 * Wire a "select all" checkbox to the row checkboxes of a server-side
 * DataTable.
 *
 * Shared by the Jobs list and the PO Manager because the subtleties are not
 * obvious and were each got wrong once already:
 *
 * - **The master is a view of the rows, not a second opinion about them.** Its
 *   three states are recomputed from the checkboxes on every change and every
 *   draw, so it cannot drift from what is on screen. A master that only ever
 *   pushes state downwards goes stale the moment a row is ticked by hand.
 * - **A synthetic `.click()` is a toggle, not an assignment**, so pressing
 *   select-all over rows that were already ticked unticks them. Assign
 *   `checked` and dispatch `change` explicitly — assignment on its own fires no
 *   event, which is the other half of the same bug.
 * - **Rows already in the wanted state are left alone**, so listeners only ever
 *   hear about real changes.
 * - Handlers are delegated from the document: a server-side table replaces
 *   every row on every draw, so anything bound to the checkboxes themselves
 *   lasts exactly one page.
 *
 * @param {object}  options
 * @param {string}  options.tableSelector  e.g. '#datatable-po-list'
 * @param {string}  options.masterSelector e.g. '#po-check-all'
 * @param {string} [options.rowSelector]   defaults to the shared bulk class
 */
export const bindSelectAll = ({
    tableSelector,
    masterSelector,
    rowSelector = '.bulk-update-checkbox',
}) => {

    const $ = window.jQuery;

    // Namespaced per table so that re-binding — a component remounting, two
    // tables on one page — replaces its own handlers rather than stacking
    // another copy on top. Stacked handlers are why select-all on the Jobs page
    // ran its toggle once per redraw.
    const namespace = `.bulkSelect${tableSelector.replace(/[^a-z0-9]/gi, '')}`;

    const rows = () => $(`${tableSelector} ${rowSelector}`);

    /**
     * Disabled when the page offers nothing to select — on the PO Manager a
     * page can be entirely cool-down rows, which render no checkbox, and a
     * master that ticks nothing reads as broken.
     *
     * Indeterminate when the selection is partial: without it, ticking three
     * rows of twenty-five by hand leaves the master blank, which says "nothing
     * selected" to anyone glancing at the header.
     */
    let applying = false;

    const refreshMaster = () => {
        // Not while select-all is working through the rows. Each row it ticks
        // announces itself, and answering those part-way through would set the
        // master indeterminate — and therefore unchecked — mid-loop, which is
        // the state the loop is reading. It ticked the first row, concluded the
        // rest already matched, and stopped.
        if (applying) {
            return;
        }

        const all = rows();
        const checked = all.filter(':checked');
        const master = $(masterSelector);

        master.prop('disabled', all.length === 0);
        master.prop('checked', all.length > 0 && checked.length === all.length);
        master.prop('indeterminate', checked.length > 0 && checked.length < all.length);
    };

    $(document).off(namespace);

    $(document).on(`change${namespace}`, masterSelector, function () {
        // Snapshotted before the loop, never read from the element inside it:
        // the rows report back as they change, and anything they cause must not
        // be able to redefine what this press asked for.
        const wanted = this.checked;

        applying = true;

        rows().each((_, el) => {
            if (el.checked === wanted) {
                return;
            }

            el.checked = wanted;
            el.dispatchEvent(new Event('change', { bubbles: true }));
        });

        applying = false;

        refreshMaster();
    });

    $(document).on(`change${namespace}`, `${tableSelector} ${rowSelector}`, refreshMaster);
    $(document).on(`draw.dt${namespace}`, tableSelector, refreshMaster);

    refreshMaster();
};
